import { APIError } from '../../utils/errors.js';
import { MOCK_SURVEYS } from '../mock-data/index.js';
import { delay, getDeviceInfo } from '../utils/helpers.js';

export class SurveyService {
	constructor(baseAPI) {
		this.api = baseAPI;
	}

	async getActiveSurveys(context = {}) {
		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(200);
			return { success: true, data: MOCK_SURVEYS };
		}

		const respondent = this._getRespondentContext(context);

		const params = {
			url:
				context.url ||
				(typeof window !== 'undefined' ? window.location.href : ''),
			...getDeviceInfo(),
			...(respondent.respondent_id && {
				respondent_id: respondent.respondent_id,
			}),
			...(respondent.email && { email: respondent.email }),
			...(context.includeEligibility !== undefined && {
				include_eligibility: context.includeEligibility,
			}),
			...(context.userProperties && {
				user_properties: context.userProperties,
			}),
		};

		return this.api._handleAuthRetry(async () => {
			const endpoint = this.api._getEndpointWithParams(
				'/widget/surveys/active',
				params
			);
			return await this.api._makeRequest(endpoint, {
				method: 'GET',
				headers: { Authorization: `Bearer ${this.api.sessionToken}` },
			});
		});
	}

	_getRespondentContext(context = {}) {
		const metadata =
			typeof this.api.getMetadata === 'function'
				? this.api.getMetadata() || {}
				: {};

		return {
			respondent_id:
				context.respondentId || context.userId || metadata.user_id || null,
			email: context.email || metadata.email || null,
		};
	}

	async submitSurveyResponse(surveyId, responseData) {
		if (!surveyId) throw new APIError(400, 'Survey ID is required');

		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(300);
			return {
				success: true,
				data: {
					id: 'mock_response_' + Date.now(),
					survey_id: surveyId,
					...responseData,
				},
				message: 'Survey response submitted successfully!',
			};
		}

		const respondent = this._getRespondentContext(responseData);
		const contact = this.api.getContactIdentity?.() || null;

		const payload = {
			answers: Array.isArray(responseData.answers) ? responseData.answers : [],
			...(responseData.feedback && { feedback: responseData.feedback }),
			...(respondent.respondent_id && {
				respondent_id: respondent.respondent_id,
			}),
			...(respondent.email && { email: respondent.email }),
			...(contact?.contactId && {
				contact_id: contact.contactId,
				contact_email: contact.contactEmail,
				contact_name: contact.contactName,
			}),
		};

		return this.api._handleAuthRetry(async () => {
			return await this.api._makeRequest(
				`/widget/surveys/${surveyId}/responses`,
				{
					method: 'POST',
					body: JSON.stringify(payload),
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${this.api.sessionToken}`,
					},
				}
			);
		});
	}

	async dismissSurvey(surveyId) {
		if (!surveyId) throw new APIError(400, 'Survey ID is required');

		await this.api._ensureSession();

		if (this.api.mock) {
			await delay(100);
			return { success: true, message: 'Survey dismissed successfully' };
		}

		return this.api._handleAuthRetry(async () => {
			return await this.api._makeRequest(
				`/widget/surveys/${surveyId}/dismiss`,
				{
					method: 'POST',
					headers: { Authorization: `Bearer ${this.api.sessionToken}` },
				}
			);
		});
	}
}
